import React, { useState, useRef, useEffect } from "react";
import { CalendarIcon, PlusIcon, TrashIcon, VideoCameraIcon, UserIcon } from '@heroicons/react/24/outline';
import { Field, Label } from '../../fieldsComponents/appointments/fieldset';
import { Combobox, ComboboxOption } from '../../fieldsComponents/appointments/combobox';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import MultiDatePicker, { DateObject } from 'react-multi-date-picker';
import DialogTimeEdit from '../../components/appointments/dialogTimeEdit';
import { Switch } from '../../fieldsComponents/appointments/switch';
import DialogRemainderEdit from '../../components/appointments/dialogRemainderEdit'; // Import the new dialog component

const BookingAppoinment = ({ disabled, formData = {}, onFormDataChange, onFieldChange }) => {
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const calendarRef = useRef(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false); // State for dialog visibility
  const [selectedDay, setSelectedDay] = useState(''); // State for selected day
  const daysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const [initialTimeInputs, setInitialTimeInputs] = useState([]); // State for initial time inputs
  // Only allow unavailableDates that are today or in the future
  const filterFutureDates = (dates) =>
    (dates || []).filter(date => {
      if (!date) return false;
      const d = typeof date === 'string' ? new Date(date) : date;
      // Remove time part for comparison
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      d.setHours(0, 0, 0, 0);
      return d >= today;
    });
  const [unavailableDates, setUnavailableDates] = useState(filterFutureDates(formData.unavailableDates));
  const [unavailableDatesRangeMode, setUnavailableDatesRangeMode] = useState(false);
  const [mode, setMode] = useState(); // State for mode
  // For Slot Duration Combobox
  const slotDurationOptions = [
    { id: '15', name: '15 minutes' },
    { id: '30', name: '30 minutes' },
    { id: '60', name: '1 hours' },
    { id: '120', name: '2 hours' },
  ];
  const [slotDurationQuery, setSlotDurationQuery] = useState('');

  // For Available Dates Combobox
  const availableDatesOptions = [
    { id: 'rolling', name: "Rolling 'X' days" },
    { id: 'range', name: 'Date Range' },
    { id: 'future', name: 'Any date in future' },
  ];
  const [availableDatesQuery, setAvailableDatesQuery] = useState('');
  const [remainderFields, setRemainderFields] = useState(formData.remainderFields || []);
  const [isRemainderDialogOpen, setIsRemainderDialogOpen] = useState(false);
  const [selectedRemainderIndex, setSelectedRemainderIndex] = useState(null);
  // Initialize showFields array with true values for each existing remainder field
  const [showFields, setShowFields] = useState(Array(formData.remainderFields?.length || 0).fill(true));

  useEffect(() => {
    // Initialize default values if they don't exist
    if (!formData.availableDatesOption) {
      onFormDataChange('availableDatesOption', 'any');
    }
    if (!formData.appointmentDuration) {
      onFormDataChange('appointmentDuration', '60');
    }
    if (formData.allowMultipleAppointments === undefined) {
      onFormDataChange('allowMultipleAppointments', false);
    }
    if (formData.sameDayAppointment === undefined) {
      onFormDataChange('sameDayAppointment', false);
    }
    // Initialize unavailable dates, filter out past dates
    if (formData.unavailableDates) {
      const filtered = filterFutureDates(formData.unavailableDates.map(date => typeof date === 'string' ? new Date(date) : date));
      setUnavailableDates(filtered);
      // Also update formData if any past dates were removed
      if (filtered.length !== formData.unavailableDates.length) {
        onFormDataChange('unavailableDates', filtered);
      }
    }
    // Initialize remainder fields
    if (formData.remainderFields) {
      setRemainderFields(formData.remainderFields);
      setShowFields(Array(formData.remainderFields.length).fill(true));
    }
  }, [formData]);

  const handleUnavailableDateChange = (date, index) => {
    // Prevent selecting past dates
    if (!date) return;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selected = new Date(date);
    selected.setHours(0, 0, 0, 0);
    if (selected < today) return;
    const newDates = [...unavailableDates];
    newDates[index] = selected;
    const filtered = filterFutureDates(newDates);
    setUnavailableDates(filtered);
    onFormDataChange('unavailableDates', filtered);
    onFieldChange();
  };

  const addDatePicker = () => {
    setUnavailableDates([...unavailableDates, null]);
  };

  const removeDatePicker = (index) => {
    const newDates = unavailableDates.filter((_, i) => i !== index);
    const filtered = filterFutureDates(newDates);
    setUnavailableDates(filtered);
    onFormDataChange('unavailableDates', filtered);
    onFieldChange();
  };
  const handleAvailableDatesChange = (e) => {
    const value = e.target.value;
    onFormDataChange('availableDatesOption', value);

    // Set default number of days to 60 when selecting rolling option
    if (value === 'rolling') {
      onFormDataChange('numberOfDays', '60');
    }

    onFieldChange();
  };

  const handleAppointmentDurationChange = (e) => {
    onFormDataChange('appointmentDuration', e.target.value);
    onFieldChange();
  };

  const handleNumberOfDaysChange = (e) => {
    onFormDataChange('numberOfDays', e.target.value);
    onFieldChange();
  };

  const handleEmailHeaderChange = (e) => {
    onFormDataChange('bookingTitle', e.target.value);
    onFieldChange();
  };

  const handleAppointmentDescriptionChange = (e) => {
    onFormDataChange('bookingdescription', e.target.value);
    onFieldChange();
  };

  const handleStartDateChange = (date) => {
    onFormDataChange('startDate', date);
    if (formData.endDate && date > formData.endDate) {
      onFormDataChange('endDate', null); // Reset end date if start date is after end date
    }
    onFieldChange();
  };

  const handleEndDateChange = (date) => {
    onFormDataChange('endDate', date);
    onFieldChange();
  };

  const toggleCalendar = () => {
    setIsCalendarOpen(!isCalendarOpen);
  };

  const handleClickOutside = (event) => {
    if (calendarRef.current && !calendarRef.current.contains(event.target)) {
      setIsCalendarOpen(false);
    }
  };

  useEffect(() => {
    if (isCalendarOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isCalendarOpen]);

  const handleEditClick = (day) => {
    setSelectedDay(day);
    const dayTimeInputs = formData?.availability?.time?.[day] || [];
    setInitialTimeInputs(dayTimeInputs);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedDay('');
  };

  const handleSave = (timeInputs) => {
    const updatedAvailability = {
      ...formData.availability,
      time: {
        ...formData.availability.time,
        [selectedDay]: timeInputs
      }
    };

    // Update the days array
    const updatedDays = Object.keys(updatedAvailability.time);

    onFormDataChange('availability', {
      days: updatedDays,
      time: updatedAvailability.time
    });
    setIsDialogOpen(false);
    onFieldChange();
  };

  const addRemainderField = () => {
    setRemainderFields([...remainderFields, { type: "", remainderType: "", remainderValue: "" }]);
    setShowFields([...showFields, false]); // Initially hide the text field
    onFieldChange();
  };

  const removeRemainderField = (index) => {
    const newRemainderFields = remainderFields.filter((_, i) => i !== index);
    const newShowFields = showFields.filter((_, i) => i !== index);
    setRemainderFields(newRemainderFields);
    setShowFields(newShowFields);
    onFieldChange();
  };

  const handleOpenRemainderDialog = (index) => {
    setSelectedRemainderIndex(index);
    setIsRemainderDialogOpen(true);
  };

  const handleCloseRemainderDialog = () => {
    setIsRemainderDialogOpen(false);
    setSelectedRemainderIndex(null);
  };

  const handleSaveRemainder = (remainderData) => {


    // Update local state
    const newRemainderFields = [...remainderFields];
    newRemainderFields[selectedRemainderIndex] = remainderData;
    setRemainderFields(newRemainderFields);

    // Show the corresponding text field after saving
    const newShowFields = [...showFields];
    newShowFields[selectedRemainderIndex] = true;
    setShowFields(newShowFields);

    // Update formData to be saved to database
    onFormDataChange('remainderFields', newRemainderFields);
    onFieldChange();

    setIsRemainderDialogOpen(false);
  };

  const handleModeChange = () => {
    const newMode = !formData.mode;
    onFormDataChange('mode', newMode);
    onFieldChange();
  };

  const handleSameDayAppointmentChange = () => {
    const newValue = !formData.sameDayAppointment;
    onFormDataChange('sameDayAppointment', newValue);
    onFieldChange();
  };

  const handleSlotCapacityChange = (value) => {
    // Convert string to boolean and update state
    const boolValue = value === "true";
    onFormDataChange('appointmentType', boolValue);
    onFieldChange();
  };

  const handleRemoveRemainder = (index) => {
    const newRemainderFields = remainderFields.filter((_, i) => i !== index);
    setRemainderFields(newRemainderFields);
    onFormDataChange('remainderFields', newRemainderFields);
    onFieldChange();
  };

  return (
    <div className="overflow-x-auto w-full">

      {/* Booking Appointments */}
      <table className="min-w-full divide-y divide-gray-300 w-full h-full">
        <thead className="bg-gray-100">
          <tr>
            {daysOfWeek.map(day => (
              <th key={day} className="px-4 py-2 sm:px-6 sm:py-3 text-center text-xs sm:text-sm font-semibold text-gray-900 whitespace-nowrap">{day}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          <tr>
            {daysOfWeek.map(day => (
              <td key={day} className="px-4 py-2 sm:px-6 sm:py-4 text-center text-xs sm:text-sm text-gray-500 space-y-2">
                {(formData?.availability?.time?.[day] || []).map((slot, index) => (
                  <div key={index} className="flex justify-center space-x-1">
                    <span>{slot.start}</span>
                    <span>-</span>
                    <span>{slot.end}</span>
                  </div>
                ))}
                {!disabled && (
                  <a
                    href="#"
                    onClick={() => handleEditClick(day)}
                    className="text-black hover:text-gray-700 block mt-1 text-xs sm:text-sm font-bold"
                    title="Edit time slots for this day"
                  >
                    Edit
                  </a>
                )}
              </td>
            ))}
          </tr>
        </tbody>
      </table>

      {/* Dialog for Editing Time */}
      {isDialogOpen && (
        <DialogTimeEdit
          isOpen={isDialogOpen}
          onClose={handleCloseDialog}
          onSave={handleSave}
          initialTimeInputs={initialTimeInputs}
          selectedDay={selectedDay}
        />
      )}



      <div className="flex flex-col sm:flex-row items-center mt-4 gap-3 sm:gap-5">
        <Field className="flex flex-row items-center w-full">
          <Label
            className={`w-40 text-sm font-medium leading-6 ${disabled ? 'text-gray-400' : 'text-gray-900'} text-left`}>
            Slot Duration
          </Label>
          <div className="flex items-center gap-2 sm:gap-4 mt-2 sm:mt-0 w-[200px] sm:w-[220px] ml-2 sm:ml-4">
            <Combobox
              options={slotDurationOptions}
              value={slotDurationOptions.find(opt => opt.id === (formData.appointmentDuration || '60')) || null}
              displayValue={opt => (opt && opt.name ? opt.name : '')}
              onChange={opt => {
                if (opt && opt.id) {
                  onFormDataChange('appointmentDuration', opt.id);
                  onFieldChange();
                }
              }}
              filter={(opt, query) => opt.name.toLowerCase().includes(query.toLowerCase())}
              disabled={disabled}
              dropdownClassName=" min-w-[220px] max-w-[280px] bg-white"
            >
              {option => (
                <ComboboxOption key={option.id} value={option} className="border-none outline-none focus:ring-0" >
                  {option.name}
                </ComboboxOption>
              )}
            </Combobox>
          </div>
        </Field>
      </div>

      {  /* Slot capacity */}
      <div className="flex flex-col sm:flex-row  mt-4 gap-3 sm:gap-3">
        <Field className="flex flex-row items-center w-full">
          <Label className={`w-40 text-sm font-medium leading-6 ${disabled ? 'text-gray-400' : 'text-gray-900'} text-left flex items-center`}>
            Slot Capacity
          </Label>
          <div className="flex items-center gap-2 sm:gap-4 mt-2 ml-5 sm:mt-0">
            <label className="flex items-center gap-1 sm:gap-2">
              <input
                type="radio"
                name="appointmentType"
                value="false"
                checked={formData.appointmentType === false}
                onChange={(e) => handleSlotCapacityChange(e.target.value)}
                className="h-3 w-3 sm:h-4 sm:w-4 text-black border-gray-300 focus:ring-black"
                disabled={disabled}
              />
              <span className={`text-xs sm:text-sm ${disabled ? 'text-gray-400' : 'text-gray-900'}`}>Single</span>
            </label>
            <label className="flex items-center gap-1 sm:gap-2">
              <input
                type="radio"
                name="appointmentType"
                value="true"
                checked={formData.appointmentType === true}
                onChange={(e) => handleSlotCapacityChange(e.target.value)}
                className="h-3 w-3 sm:h-4 sm:w-4 text-black border-gray-300 focus:ring-black"
                disabled={disabled}
              />
              <span className={`text-xs sm:text-sm ${disabled ? 'text-gray-400' : 'text-gray-900'}`}>Multiple</span>
            </label>
          </div>
        </Field>
      </div>

      {/* Same Day Appointment Toggle */}
      <div className="flex flex-col sm:flex-row items-center mt-4 gap-3 sm:gap-5">
        <Field className="flex flex-row items-center w-full">
          <Label
            className={`w-40 text-sm font-medium leading-6 ${disabled ? 'text-gray-400' : 'text-gray-900'} text-left`}>Same Day Appointment</Label>
          <div className="flex items-center gap-2 sm:gap-4 mt-2 ml-5 sm:mt-0">
            <Switch
              name="sameDayAppointment"
              checked={formData.sameDayAppointment === true}
              onChange={handleSameDayAppointmentChange}
              className={`cursor-pointer !transition-all !duration-300 
                ${formData.sameDayAppointment ? '!bg-black' : '!bg-gray-200'} 
                !border-0 !outline-none !ring-0 !ring-offset-0 hover:!border-0 hover:!ring-0 hover:!outline-none focus:!ring-0 focus:!ring-offset-0 focus:!border-0`}
              disabled={disabled}
            />
            <span className={`ml-2 text-sm ${disabled ? 'text-gray-400' : 'text-gray-900'} flex items-center justify-center`}>
              {formData.sameDayAppointment ? (
                <>
                  <span role="img" aria-label="calendar" className="mr-1">📆</span>
                  <span className="mt-0">Accept appointment same date</span>
                </>
              ) : (
                <>
                  <span role="img" aria-label="calendar-next" className="mr-1">📅</span>
                  <span className="mt-0">Appointment will be accepted D+1</span>
                </>
              )}
            </span>
          </div>
        </Field>
      </div>

      {/* Appointment Mode - Moved up from below */}
      <div className="flex flex-col sm:flex-row items-center mt-4 gap-3 sm:gap-5">
        <Field className="flex flex-row items-center w-full">
          <Label
            className={`w-40 text-sm font-medium leading-6 ${disabled ? 'text-gray-400' : 'text-gray-900'} text-left`}>Online Appointment</Label>
          <div className="flex items-center gap-2 sm:gap-4 mt-2 ml-5 sm:mt-0">
            <Switch
              name="onlineAppointment"
              checked={formData.mode === true}
              onChange={handleModeChange}
              className={`cursor-pointer !transition-all !duration-300 
                ${formData.mode ? '!bg-black' : '!bg-gray-200'} 
                !border-0 !outline-none !ring-0 !ring-offset-0 hover:!border-0 hover:!ring-0 hover:!outline-none focus:!ring-0 focus:!ring-offset-0 focus:!border-0`}
              disabled={disabled}
            />
            <span className={`ml-2 text-sm ${disabled ? 'text-gray-400' : 'text-gray-900'} flex items-center justify-center`}>
              {formData.mode ? (
                <>
                  <span role="img" aria-label="headphones" className="mr-1">🎧</span>
                  <span className="mt-0">Google Meet</span>
                </>
              ) : (
                <>
                  <span role="img" aria-label="person" className="mr-1">🚶</span>
                  <span className="mt-0">Walk-in</span>
                </>
              )}
            </span>
          </div>
        </Field>
      </div>

      {/* Remainder Fields - Moved up from below */}
      <div className="flex flex-row items-start mt-4 gap-3">
        <Field className="flex flex-row items-start w-full">
          <Label className={`w-40 text-sm font-medium leading-6 ${disabled ? 'text-gray-400' : 'text-gray-900'} text-left mt-2`}>
            Reminders
          </Label>
          <div className="flex-1 flex flex-wrap items-start gap-3 pl-5">
            {remainderFields.map((remainder, index) => (
              showFields[index] && remainder.type && (
                <div key={index} className="inline-flex items-center gap-2">
                  <input
                    type="text"
                    value={`${remainder.type} before ${remainder.remainderValue} ${remainder.remainderType}`}
                    onClick={() => handleOpenRemainderDialog(index)}
                    className={`w-32 sm:w-36 h-9 border border-gray-300 rounded-lg text-sm
                      focus:ring-black-500 focus:border-black-500 
                      ${disabled ? 'bg-gray-200 text-gray-400' : 'bg-white text-gray-900'}`}
                    readOnly
                  />
                  <button
                    onClick={() => handleRemoveRemainder(index)}
                    className="rounded-md bg-white text-red-600 hover:bg-gray-100 
                      focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                    disabled={disabled}
                    title="Remove remainder"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              )
            ))}
            <button
              onClick={() => {
                addRemainderField();
                setTimeout(() => {
                  setSelectedRemainderIndex(remainderFields.length);
                  setIsRemainderDialogOpen(true);
                }, 0);
              }}
              className="rounded-md bg-black text-white p-[2px] hover:bg-gray-800 
             focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2 mt-2 disabled:opacity-50"
              disabled={disabled}
              title="Add new remainder"
            >
              <PlusIcon className="h-4 w-4 stroke-[2]" />
            </button>
          </div>
        </Field>
      </div>

      {/* Available Dates */}
      <div className="flex flex-col sm:flex-row items-center mt-4 gap-3 sm:gap-5">
        <Field className="flex flex-row items-center w-full">
          <Label className={`w-40 text-sm font-medium leading-6 ${disabled ? 'text-gray-400' : 'text-gray-900'} text-left`}>
            Available Dates
          </Label>
          <div className="flex items-center gap-2 sm:gap-4 mt-2 sm:mt-0 w-[200px] sm:w-[220px] ml-6 sm:ml-4">
            <Combobox
              options={availableDatesOptions}
              value={availableDatesOptions.find(opt => opt.id === (formData.availableDatesOption || 'future')) || null}
              displayValue={opt => (opt && opt.name ? opt.name : '')}
              onChange={opt => {
                if (opt && opt.id) {
                  handleAvailableDatesChange({ target: { value: opt.id } });
                }
              }}
              filter={(opt, query) => opt.name.toLowerCase().includes(query.toLowerCase())}
              disabled={disabled}
              
              dropdownClassName="min-w-[220px] max-w-[220px] bg-white"
            >
              {option => (
                <ComboboxOption key={option.id} value={option}>
                  {option.name}
                </ComboboxOption>
              )}
            </Combobox>
          </div>
        </Field>
      </div>

      {/* Number of Days */}
      {formData.availableDatesOption === 'rolling' && (
        <div className="flex flex-col sm:flex-row  mt-4 gap-3 sm:gap-3">
          <Field className="flex flex-row items-center w-full">
            <Label className={`w-40 text-sm font-medium leading-6 ${disabled ? 'text-gray-400' : 'text-gray-900'} text-left`}>Number of Days <span className="text-red-500">*</span></Label>
            <div className="flex items-center gap-2 sm:gap-4 mt-2 sm:mt-0">
              <input
                type="number"
                name="numberOfDays"
                value={formData.numberOfDays}
                onChange={handleNumberOfDaysChange}

                className={`appearance-none border border-gray-300 rounded-lg shadow-sm focus:ring focus:ring-black-300 
                  w-[120px] sm:w-[80px] h-9 text-xs sm:text-sm text-gray-900 ml-2 sm:ml-4
                  ${disabled ? 'bg-gray-200 text-gray-400' : 'bg-white text-gray-900'}`}

                disabled={disabled}
                placeholder="Days"
              />
              <span className={`text-xs ${disabled ? 'text-gray-400 text-sm' : 'text-gray-900 text-sm'}`}>days</span>
            </div>
          </Field>
        </div>
      )}

      {/* Date Range */}
      {formData.availableDatesOption === 'range' && (
        <div className="flex flex-col gap-5">
          <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-5">
            <Field className="flex flex-row items-center w-full">
              <Label className={`w-40 text-sm font-medium leading-6 mt-4 ${disabled ? 'text-gray-400' : 'text-gray-900'} text-left`}>
                Start Date <span className="text-red-500">*</span>
              </Label>
              <div className="flex items-center gap-2 sm:gap-4 mt-2 sm:mt-0">
                <DatePicker
                  selected={formData.startDate}
                  onChange={handleStartDateChange}
                  className={`w-[200px] sm:w-[220px] h-9 border border-gray-300 text-sm rounded-lg ml-2 sm:ml-4
                    focus:ring-black-500 focus:border-black-500 mt-5
                    ${disabled ? 'bg-gray-200 text-gray-400' : 'bg-white text-gray-900'}`}
                  dateFormat="yyyy-MM-dd"
                  minDate={new Date()}
                  placeholderText="Select a start date"
                  disabled={disabled}
                />
              </div>
            </Field>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-5">
            <Field className="flex flex-row items-center w-full">
              <Label className={`w-40 text-sm font-medium leading-6 ${disabled ? 'text-gray-400' : 'text-gray-900'} text-left`}>
                End Date <span className="text-red-500">*</span>
              </Label>
              <div className="flex items-center gap-2 sm:gap-4 mt-2 sm:mt-0">
                <DatePicker
                  selected={formData.endDate}
                  onChange={handleEndDateChange}
                  className={`w-[200px] sm:w-[220px] h-9 border border-gray-300 text-sm rounded-lg ml-2 sm:ml-4
                    focus:ring-black-500 focus:border-black-500 
                    ${disabled ? 'bg-gray-200 text-gray-400' : 'bg-white text-gray-900'}`}
                  dateFormat="yyyy-MM-dd"
                  minDate={formData.startDate ? new Date(formData.startDate.getTime()) : null}
                  disabled={disabled}
                  placeholderText="Select an end date"
                />
              </div>
            </Field>
          </div>
        </div>
      )}

      {/* Unavailable Dates */}
      <div className="flex flex-row items-start mt-4 gap-3">
        <Field className="flex flex-row items-start w-full">
          <Label
            className={`w-40 text-sm font-medium mt-1 leading-6 ${disabled ? 'text-gray-400' : 'text-gray-900'} text-left`}>
            Unavailable Dates
          </Label>
          <div className="flex-1 flex flex-wrap items-start gap-3 pl-4">
            {unavailableDates.map((date, index) => (
              <div
                key={index}
                className="inline-flex items-center gap-2"
              >
                <DatePicker
                  selected={date}
                  onChange={(date) => handleUnavailableDateChange(date, index)}
                  className={`w-32 sm:w-36 h-9 border border-gray-300 rounded-lg 
                    focus:ring-black-500 focus:border-black-500 text-sm
                    ${disabled ? 'bg-gray-200 text-gray-400' : 'bg-white text-gray-900'}`}
                  minDate={new Date()}
                  placeholderText="Select date"
                  dateFormat="yyyy-MM-dd"
                  disabled={disabled}
                  filterDate={d => {
                    // Only allow today or future
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    d.setHours(0, 0, 0, 0);
                    return d >= today;
                  }}
                />
                <button
                  onClick={() => removeDatePicker(index)}
                  className="rounded-md bg-white text-red-600 hover:bg-gray-100 
                    focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                  disabled={disabled}
                  title="Remove date"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            ))}
            <button
              onClick={addDatePicker}
              className="rounded-md bg-black text-white p-[2px] hover:bg-gray-800 
              focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2 mt-2 disabled:opacity-50"
              disabled={disabled}
              title="Add new unavailable date"
            >
              <PlusIcon className="h-4 w-4 stroke-[2]" />
            </button>
          </div>
        </Field>
      </div>

      {/* Dialog for Editing Remainder */}
      {isRemainderDialogOpen && (
        <DialogRemainderEdit
          isOpen={isRemainderDialogOpen}
          onClose={handleCloseRemainderDialog}
          onSave={handleSaveRemainder}
          initialRemainderData={remainderFields[selectedRemainderIndex] || { type: 'email', remainderType: 'days', remainderValue: '' }}
        />
      )}
    </div>

  );
};

export default BookingAppoinment;